<template>
  <div class="status-wrapper">
    <Card class="status-card">
      <template #content>
        <div class="status-content">
          <!-- Paid Status - Waiting for Verification -->
          <div v-if="paymentStatus === 'Paid'" class="status-section pending">
            <i class="pi pi-clock status-icon"></i>
            <h2 class="status-title">Payment Verification Pending</h2>
            <p class="status-description">
              Thank you for your payment! Your transaction has been recorded and is currently under verification.
              Our team will review and approve it shortly.
            </p>

            <div class="info-card">
              <div class="info-row">
                <span class="info-label">Status:</span>
                <Tag value="Pending Verification" severity="warning" />
              </div>
              <div class="info-row">
                <span class="info-label">Submitted:</span>
                <span class="info-value">{{ new Date().toLocaleString('en-IN') }}</span>
              </div>
            </div>

            <div class="action-section">
              <p class="action-hint">
                <i class="pi pi-info-circle"></i>
                Verification usually takes 2-24 hours. You'll receive an email once approved.
              </p>

              <ANButton
                label="Check Verification Status"
                variant="primary"
                icon="pi pi-refresh"
                :loading="isChecking"
                @click="checkPaymentStatus"
                class="check-btn"
              />

              <ANButton
                label="Contact Support"
                variant="secondary"
                icon="pi pi-envelope"
                @click="contactSupport"
                class="support-btn"
              />
            </div>
          </div>

          <!-- Verified State -->
          <div v-else-if="paymentStatus === 'Verified'" class="status-section verified">
            <i class="pi pi-check-circle status-icon"></i>
            <h2 class="status-title">Payment Verified!</h2>
            <p class="status-description">
              Congratulations! Your payment has been verified and your subscription is now active.
              Let's continue with your investment profile setup.
            </p>

            <div class="success-card">
              <div class="success-row">
                <i class="pi pi-shield-check"></i>
                <span>Full Access Activated</span>
              </div>
              <div class="success-row">
                <i class="pi pi-user-check"></i>
                <span>Premium Features Unlocked</span>
              </div>
              <div class="success-row">
                <i class="pi pi-calendar-check"></i>
                <span>Valid for 1 Year</span>
              </div>
            </div>

            <ANButton
              label="Continue to Risk Assessment"
              variant="primary"
              icon="pi pi-arrow-right"
              icon-position="right"
              size="large"
              @click="proceedToRiskAssessment"
              class="proceed-btn"
            />
          </div>

          <!-- Initiated Status - Payment Not Yet Made -->
          <div v-else-if="paymentStatus === 'Initiated'" class="status-section initiated">
            <i class="pi pi-info-circle status-icon"></i>
            <h2 class="status-title">Payment Not Completed</h2>
            <p class="status-description">
              Your payment is still pending. Please complete the payment to activate your subscription.
            </p>

            <div class="action-section">
              <ANButton
                label="Go to Payment Page"
                variant="primary"
                icon="pi pi-credit-card"
                @click="goToPayment"
                class="payment-btn"
              />

              <ANButton
                label="Contact Support"
                variant="secondary"
                icon="pi pi-envelope"
                @click="contactSupport"
                class="support-btn"
              />
            </div>
          </div>

          <!-- Not Found / Error State -->
          <div v-else class="status-section error">
            <i class="pi pi-exclamation-triangle status-icon"></i>
            <h2 class="status-title">Payment Status Unknown</h2>
            <p class="status-description">
              We couldn't find your payment record. This might be because:
            </p>

            <ul class="error-reasons">
              <li>Payment verification is still in progress</li>
              <li>There was an issue with your payment submission</li>
              <li>The payment details need to be re-submitted</li>
            </ul>

            <div class="action-section">
              <ANButton
                label="Try Again"
                variant="primary"
                icon="pi pi-refresh"
                :loading="isChecking"
                @click="checkPaymentStatus"
                class="retry-btn"
              />

              <ANButton
                label="Re-submit Payment Details"
                variant="secondary"
                icon="pi pi-replay"
                @click="resubmitPayment"
                class="resubmit-btn"
              />

              <ANButton
                label="Contact Support"
                variant="secondary"
                icon="pi pi-envelope"
                @click="contactSupport"
                class="support-btn"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useUserStore } from '@/store/user-store'
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import ANButton from '@/components/common/AnButton.vue'

const store = useUserStore()

const paymentStatus = ref('Pending') // 'Pending', 'Paid', 'Verified', 'Error'
const isChecking = ref(false)

// Check payment status on mount
onMounted(async () => {
  await checkPaymentStatus()
  document.title = 'Payment Status - AssetNivesh'
})

// Check payment status from backend
const checkPaymentStatus = async () => {
  try {
    isChecking.value = true

    const response = await store.getCustomerPaymentStatus({})
    console.log('Payment status response:', response)

    if (response) {
      const status = response.PaymentStatus || response.paymentStatus
      console.log('Payment status retrieved:', status)

      paymentStatus.value = status || 'Error'

      // Reload dashboard data to update payment status in store
      await store.loadDashboardData(store.user.email)

      if (status === 'Verified') {
        store.fireAlert('Success', 'Your payment has been verified! You can now proceed.')
      } else if (status === 'Paid') {
        store.fireAlert('Info', 'Your payment is pending verification. Please check back later.')
      } else if (status === 'Initiated') {
        store.fireAlert('Warning', 'Payment not completed. Please complete your payment.')
      } else {
        store.fireAlert('Warning', 'Unable to determine payment status.')
      }
    } else {
      throw new Error('No response data')
    }
  } catch (error) {
    console.error('Error checking payment status:', error)
    paymentStatus.value = 'Error'
    store.fireAlert('Error', 'Could not retrieve payment status. Please try again.')
  } finally {
    isChecking.value = false
  }
}

// Proceed to risk assessment
const proceedToRiskAssessment = async () => {
  try {
    // Reload dashboard data to ensure payment status is updated
    await store.loadDashboardData(store.user.email)
    
    // Check if risk assessment is needed
    const portfolioTargetAlloc = store.dashboardData?.portfolioTargetAlloc || []
    const hasCompletedRiskAssessment = Array.isArray(portfolioTargetAlloc) && portfolioTargetAlloc.length > 0
    
    if (hasCompletedRiskAssessment) {
      // Risk assessment already done, go to dashboard
      store.setTabView('Dashboard')
      store.fireAlert('Success', 'Welcome back! Your portfolio is ready.')
    } else {
      // Navigate to Model Portfolio for risk assessment
      store.setTabView('MyModelPortfolio')
      store.fireAlert('Success', 'Welcome! Let\'s assess your risk profile for personalized recommendations.')
    }
  } catch (error) {
    console.error('Error proceeding to risk assessment:', error)
    store.fireAlert('Error', 'Failed to proceed. Please try again.')
  }
}// Contact support
const contactSupport = () => {
  store.setTabView('Operations Support')
  store.fireAlert('Info', 'Please describe your payment issue and our team will assist you.')
}

// Go to payment page
const goToPayment = () => {
  store.setTabView('PaymentQRCode')
}

// Resubmit payment details
const resubmitPayment = () => {
  store.setTabView('PaymentVerification')
}
</script>

<style scoped>
.status-wrapper {
  padding: 24px;
  max-width: 700px;
  margin: 0 auto;
}

.status-card {
  box-shadow: var(--an-card-shadow);
}

.status-content {
  min-height: 400px;
}

.status-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
}

.status-icon {
  font-size: 4rem;
  margin-bottom: 24px;
}

.pending .status-icon {
  color: #ff9800;
  animation: pulse 2s ease-in-out infinite;
}

.verified .status-icon {
  color: #28a745;
  animation: checkBounce 0.6s ease-out;
}

.error .status-icon {
  color: #dc3545;
}

.initiated .status-icon {
  color: #ffc107;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes checkBounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.status-title {
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
  font-size: 2rem;
  font-weight: 700;
}

.status-description {
  color: var(--an-text-secondary);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 32px 0;
  max-width: 500px;
}

/* Info Card */
.info-card {
  width: 100%;
  max-width: 400px;
  padding: 24px;
  background: #fff3cd;
  border-radius: 8px;
  border: 2px solid #ffc107;
  margin-bottom: 32px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.info-row:not(:last-child) {
  border-bottom: 1px solid #ffe69c;
}

.info-label {
  color: #856404;
  font-weight: 600;
}

.info-value {
  color: #533f03;
  font-weight: 500;
}

/* Success Card */
.success-card {
  width: 100%;
  max-width: 400px;
  padding: 24px;
  background: #d4edda;
  border-radius: 8px;
  border: 2px solid #28a745;
  margin-bottom: 32px;
}

.success-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: #155724;
  font-weight: 600;
}

.success-row i {
  color: #28a745;
  font-size: 1.3rem;
}

.success-row:not(:last-child) {
  border-bottom: 1px solid #c3e6cb;
}

/* Error Reasons */
.error-reasons {
  text-align: left;
  max-width: 400px;
  margin: 0 auto 32px;
  padding-left: 20px;
  color: var(--an-text-secondary);
}

.error-reasons li {
  margin-bottom: 8px;
  line-height: 1.5;
}

/* Action Section */
.action-section {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--an-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.action-hint i {
  color: #2196f3;
}

.check-btn,
.proceed-btn,
.retry-btn,
.payment-btn,
.resubmit-btn,
.support-btn {
  width: 100%;
}

/* Responsive design */
@media (max-width: 768px) {
  .status-wrapper {
    padding: 16px;
  }

  .status-title {
    font-size: 1.6rem;
  }

  .status-icon {
    font-size: 3rem;
  }

  .status-section {
    padding: 16px;
  }
}
</style>
